"use client";

const MAX_ELECTIVE_SUBJECTS = 6;

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Minus } from "lucide-react";
import { filteredSubjects } from "@/lib/data/subjects/subjects";
import { categoryAGrades, csdGrades, categoryCGrades, koreanGrades, japaneseGrades, urduGrades } from "@/lib/data/subjects/constants";
import {
  useUserPreferences,
  logPreferenceSave,
  markPreferenceInteraction,
} from "@/contexts/UserPreferencesContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/lib/utils/translate";
import { tokenizeString } from "@/lib/utils/tokenizeString";
import { CATEGORY_A_ELECTIVE, CATEGORY_B_SUBJECTS as PICKABLE_CATEGORY_B, CATEGORY_C_SUBJECTS as PICKABLE_CATEGORY_C } from "@/constants";
import { DseResultsSection } from "@/components/DseResultsSection";
import ResultsTable from "@/components/ResultsTable";
import Swal from "sweetalert2";
import { getAppTitle } from "@/lib/appTitle";
import { logActivity } from "@/lib/client-activity-log";
import {
  type ScoreTriple,
  translateGrade,
  getGradeOptionsForSubject,
  CORE_SUBJECTS,
  subjectCategories
} from "@/common";

/*
 * Subject lists. These are static lookups over bundled data — no props, no state — so they are
 * built once per module rather than on every render, and are available to the state initialisers
 * below, which run before the component body does.
 */
const ALL_ELECTIVE_SUBJECTS = filteredSubjects("A", "Elective");
const CATEGORY_B_SUBJECTS = filteredSubjects("B");
const CATEGORY_C_LANGUAGES = filteredSubjects("C", "Other Languages");
const CATEGORY_C_LITERATURE = filteredSubjects("C", "Elective");
const COMBINED_ELECTIVES = [
  ...ALL_ELECTIVE_SUBJECTS,
  ...CATEGORY_B_SUBJECTS,
  ...CATEGORY_C_LANGUAGES,
  ...CATEGORY_C_LITERATURE,
];

/**
 * Every elective name that may legitimately appear in saved preferences.
 *
 * The two subject lists in this app do not agree: AddNewElective offers the names in
 * data/subjects.json (via @/constants), while COMBINED_ELECTIVES above is the hand-written list in
 * subjects.ts. Twenty-odd subjects — the ApL ones, and the stranded variants such as "Business,
 * Accounting and Financial Studies (Accounting)" — can be picked but do not appear in the second
 * list. Validating a restore against subjects.ts alone therefore threw those electives away on
 * reload, and the save effect then wrote the truncated list back over the stored one. The union is
 * what "a subject the user could have chosen" actually means.
 */
const SELECTABLE_ELECTIVE_NAMES = new Set<string>([
  ...COMBINED_ELECTIVES.map((subject: { subject: string }) => subject.subject),
  ...CATEGORY_A_ELECTIVE,
  ...PICKABLE_CATEGORY_B,
  ...PICKABLE_CATEGORY_C,
]);

const GROUPED_SUBJECTS = Object.entries(subjectCategories).map(([categoryName, subjectNames]) => ({
  name: categoryName,
  subjects: COMBINED_ELECTIVES.filter((subject: { subject: string }) =>
    subjectNames.includes(subject.subject)
  ),
}));

/** Every core subject with no grade picked. "N/A" is the stored "not set" value. */
function blankCoreScores(): Record<string, ScoreTriple> {
  const scores: Record<string, ScoreTriple> = {};
  CORE_SUBJECTS.forEach((s) => {
    scores[s.subject] = { current: "N/A", min: "N/A", max: "N/A" };
  });
  return scores;
}

/** A grade the user actually picked. "N/A" and "" are the stored "not set" values. */
function isGradeSet(grade: string | undefined): boolean {
  return Boolean(grade) && grade !== "N/A";
}

/**
 * Restored grades laid over what is already on screen, without ever overruling one.
 *
 * The preference pipeline can legitimately report stale grades before it reports the current ones —
 * a cold load resolves auth and then the database before the earlier steps are behind.
 * Assigning those intermediate results straight into state is what un-selected level buttons that
 * were already selected. So a restore may only ever *fill a blank*: the grade on screen is the most
 * recent thing the user asked for, and nothing arriving later is allowed to replace it.
 *
 * Letting a saved grade win over an on-screen one is what lost a *changed* level. Picking a new
 * level on a subject that already had one leaves `scores` ahead of `preferences.homeSubjectScores`
 * until the save below catches up, and any restore running in between — one runs whenever the
 * elective list or the active tab changes, and switching to the results table does exactly that —
 * put the old level back, on screen and then in the database. Reloading showed the old level
 * because the old level is what got stored.
 *
 * The subjects that survive are the ones `restored` describes *plus* `keepElectives` — the elective
 * rows this same restore is about to put on screen. Keying on `restored` alone is what lost the
 * elective grades: `restored` is `blankCoreScores()` overlaid with the *saved* scores, so the four
 * core subjects are always in it, but an elective is only in it once its grade has been written
 * back to preferences. Any restore that ran before that write — and one runs on every change to the
 * elective list or the active tab — dropped the elective's entry entirely, taking the level the
 * user had just picked with it, and the next save then stored the grade as absent. Core subjects
 * never showed the bug because blankCoreScores() keeps them in `restored` and the rule above then
 * protects their grade.
 *
 * Removing an elective still removes its grade: removeElective() deletes the entry from `scores`
 * outright, and a restore that no longer lists the elective leaves it out of `keepElectives`. An
 * explicit clear (the sidebar's "Clear All Selections") does not come through here at all —
 * clearSelections() records its own signature so this effect recognises it as already applied.
 */
function mergeRestoredScores(
  current: Record<string, ScoreTriple>,
  restored: Record<string, ScoreTriple>,
  keepElectives: readonly string[]
): Record<string, ScoreTriple> {
  const keepSetGrade = (restoredGrade: string, currentGrade: string | undefined): string =>
    isGradeSet(currentGrade) ? (currentGrade as string) : restoredGrade;

  const notSet: ScoreTriple = { current: "N/A", min: "N/A", max: "N/A" };
  const merged: Record<string, ScoreTriple> = {};
  for (const subjectName of new Set([...Object.keys(restored), ...keepElectives])) {
    // A subject the restore has nothing to say about keeps whatever is on screen, because
    // "not set" never beats a grade the user picked.
    const restoredTriple = restored[subjectName] ?? notSet;
    const existing = current[subjectName];
    merged[subjectName] = {
      current: keepSetGrade(restoredTriple.current, existing?.current),
      min: keepSetGrade(restoredTriple.min, existing?.min),
      max: keepSetGrade(restoredTriple.max, existing?.max),
    };
  }
  return merged;
}

/** Two score maps holding the same grades for the same subjects. */
function sameScoreMaps(
  a: Record<string, ScoreTriple>,
  b: Record<string, ScoreTriple>
): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((subjectName) => {
    const left = a[subjectName];
    const right = b[subjectName];
    return (
      right !== undefined &&
      left.current === right.current &&
      left.min === right.min &&
      left.max === right.max
    );
  });
}

/**
 * The DSE inputs a given set of saved preferences describes.
 *
 * This is deliberately a plain function rather than an effect: it seeds `useState` on the very
 * first render, so a returning user's grades are painted already selected. Restoring them from an
 * effect instead meant render 1 drew every core subject *unselected* and render 2 corrected it —
 * a visible change, which is what the grade buttons were flickering through. The elective rows
 * never flickered because they do not exist on render 1 at all; they mount already correct. Now
 * the core rows behave the same way.
 *
 * The effect below still calls this, for the one case the initialiser cannot cover: a cold page
 * load, where preferences are still loading at mount and arrive a moment later.
 */
function restoreFromPreferences(preferences: {
  homeElectiveSubjects?: string[];
  homeSubjectScores?: Record<string, ScoreTriple>;
}): { electives: string[]; scores: Record<string, ScoreTriple>; signature: string } {
  const electives = (preferences.homeElectiveSubjects || []).filter((subjectName) =>
    SELECTABLE_ELECTIVE_NAMES.has(subjectName)
  );

  const saved = preferences.homeSubjectScores;
  const scores =
    saved && Object.keys(saved).length > 0 ? { ...blankCoreScores(), ...saved } : blankCoreScores();

  return {
    electives,
    scores,
    // Same shape the restore effect compares against, so a seeded mount is recognised as
    // already-applied and does not re-set identical state.
    signature: JSON.stringify({ savedElectives: electives, restoredScores: scores }),
  };
}

export default function HomePage() {
  const { preferences, updatePreferences, isLoading: preferencesLoading } = useUserPreferences();
  const { translate, language } = useLanguage();
  const appTitle = getAppTitle(language);

  // Seeded once, on the first render of this mount. Navigating back from /recommended remounts this
  // component with preferences already loaded, so the saved grades are on screen in the first
  // painted frame instead of appearing a render later.
  const [seeded] = useState(() => restoreFromPreferences(preferences));

  /**
   * Whether the grade buttons are showing a state the app is sure of.
   *
   * The core subject rows are static — they are on screen from the first render, grades or no
   * grades — so on a cold load they used to paint every level *unselected* and then repaint the
   * saved ones a moment later, once auth had resolved and preferences had arrived. That repaint is
   * the flicker: it reads as the selection being erased and then restored. The elective rows never
   * had it because they are driven by the saved data itself, so they simply do not exist until
   * that data does.
   *
   * The core rows now behave the same way: until the saved grades are known, the buttons are laid
   * out but not painted, so no level is ever shown selected and then taken back. It is a latch —
   * once ready, always ready — because the preferences context flips back to "loading" on an auth
   * change mid-session, and hiding grades the user is already looking at would be a new flicker.
   */
  const [inputsReady, setInputsReady] = useState(() => !preferencesLoading);
  useEffect(() => {
    if (inputsReady) return;
    if (!preferencesLoading) {
      setInputsReady(true);
      return;
    }
    // Safety net: preferences that never finish loading (auth stuck, network gone) must not leave
    // the grade buttons invisible. Showing them blank is wrong; showing them never is worse.
    const giveUpId = setTimeout(() => setInputsReady(true), 3000);
    return () => clearTimeout(giveUpId);
  }, [preferencesLoading, inputsReady]);

  const [showElectiveDialog, setShowElectiveDialog] = useState(false);
  const [selectedElectives, setSelectedElectives] = useState<string[]>(seeded.electives);
  const [tempSelectedElectives, setTempSelectedElectives] = useState<string[]>(seeded.electives);
  const [removingSubjects, setRemovingSubjects] = useState<Set<string>>(new Set());

  const [showResults, setShowResults] = useState(false); // Delayed until initial load completes
  const [activeTab, setActiveTab] = useState<"all" | "recommended" | "favourites" | "eligible" | "new">(
    () => (preferences.homeActiveTab as "all" | "recommended" | "favourites" | "eligible" | "new") || "all"
  );

  // Use refs to track initial load and prevent save loops
  const isInitialLoadRef = useRef(true);
  const appliedPreferencesSignatureRef = useRef<string | null>(seeded.signature);

  const [scores, setScores] = useState<Record<string, ScoreTriple>>(seeded.scores);
  const [calculatedScores, setCalculatedScores] = useState<Record<string, ScoreTriple>>(seeded.scores);

  /**
   * The grades as they are on screen right now, readable by an effect that is not itself keyed on
   * them. The elective save below carries them, so the elective list it publishes can never be
   * ahead of the score map it publishes with it — a gap everything downstream (the restore effect,
   * and the database write behind it) reads as "these electives have no grades". Declared before
   * the effects below so it is up to date by the time they run.
   */
  const scoresRef = useRef(scores);
  useEffect(() => {
    scoresRef.current = scores;
  }, [scores]);
  const [resultsKey, setResultsKey] = useState(0); // Force re-render key for ResultsTable

  /**
   * Publish grades to preferences the moment they change (this does NOT trigger a recalculation —
   * the results table is driven by `calculatedScores`).
   *
   * Picking a level does not wait for this — handleGradeSelect publishes it from the click handler
   * itself, so it is stored before the recalculation starts. This covers every other way `scores`
   * moves: adding and removing an elective, and the merge the restore effect performs. A value
   * already published arrives here unchanged, and updatePreferences drops it.
   *
   * It used to sit behind its own one-second debounce, which bought nothing but a second in which
   * `preferences.homeSubjectScores` disagreed with the grades on screen, and every reader of
   * preferences — the restore effect, the sidebar, the database — saw the stale copy.
   */
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    updatePreferences({ homeSubjectScores: scores });
  }, [scores, updatePreferences]);

  const calculateResults = useCallback(() => {
    setActiveTab("all");
    // Defer so the browser paints the tab change before the heavy mount
    requestAnimationFrame(() => {
      setTimeout(() => {
        setCalculatedScores(scores);
        setResultsKey(prev => prev + 1);
        setShowResults(true);
        // Remember that results are on screen so returning to this page (e.g.
        // from /recommended) restores them instead of clearing them.
        updatePreferences({ homeShowResults: true, homeActiveTab: "all" });
      }, 0);
    });
  }, [scores, updatePreferences]);

  // Picking a grade recalculates on its own, 100ms after the last click, so a
  // burst of taps across subjects costs one calculation instead of one each.
  // The timer calls through a ref because calculateResults closes over `scores`
  // as they were at click time, which is one render behind the grade just set.
  const calculateResultsRef = useRef(calculateResults);
  useEffect(() => {
    calculateResultsRef.current = calculateResults;
  }, [calculateResults]);

  const gradeCalcTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (gradeCalcTimerRef.current) clearTimeout(gradeCalcTimerRef.current);
  }, []);

  const handleTabChange = useCallback(
    (tab: typeof activeTab) => {
      setActiveTab(tab);
      updatePreferences({ homeActiveTab: tab });
    },
    [updatePreferences]
  );

  // Reset refs on unmount to allow reload on next visit
  useEffect(() => {
    return () => {
      isInitialLoadRef.current = true;
      appliedPreferencesSignatureRef.current = null;
    };
  }, []);

  // Listen for redraw event from sidebar
  useEffect(() => {
    const handleRedrawEvent = () => {
      setResultsKey(prev => prev + 1);
    };

    const handleDebugEvent = () => {};

    window.addEventListener('debug-programme-results', handleDebugEvent);
    window.addEventListener('redraw-programme-results', handleRedrawEvent);

    return () => {
      window.removeEventListener('redraw-programme-results', handleRedrawEvent);
      window.removeEventListener('debug-programme-results', handleDebugEvent);
    };
  }, []); // Empty deps to register once

  // Apply saved preferences that were not available when state was seeded — a cold page load, where
  // preferences are still loading at mount. On a client-side navigation the seed already matched and
  // the signature check below makes this a no-op, so nothing re-renders and nothing flickers.
  useEffect(() => {
    if (preferencesLoading) return;

    const { electives: savedElectives, scores: restoredScores, signature: nextSignature } =
      restoreFromPreferences(preferences);

    if (appliedPreferencesSignatureRef.current !== nextSignature) {
      appliedPreferencesSignatureRef.current = nextSignature;
      setSelectedElectives(savedElectives);
      /*
       * Merged rather than assigned, so a restore can only ever add a grade — see
       * mergeRestoredScores(). Nothing the user can see gets un-selected by a later load step,
       * and the electives going on screen keep the grades they already have.
       *
       * Returning `prev` unchanged when the merge produces the same grades is what stops this
       * echoing. Publishing a grade changes preferences.homeSubjectScores, which is a dependency
       * of this effect, so every click ran a restore whose result was by definition the grades
       * already on screen — but mergeRestoredScores always builds a fresh object, so `scores`
       * changed identity, the effect that publishes it fired again, and each click cost a second
       * identical POST. React bails out of a state update that returns the previous value, so the
       * chain now stops here.
       */
      const applyRestored = (prev: Record<string, ScoreTriple>) => {
        const merged = mergeRestoredScores(prev, restoredScores, savedElectives);
        return sameScoreMaps(prev, merged) ? prev : merged;
      };
      setScores(applyRestored);
      setCalculatedScores(applyRestored);
    }

    // Restore the tab the user was last on, so returning to this page (e.g. from
    // /recommended) lands where they left off.
    if (isInitialLoadRef.current && preferences.homeActiveTab) {
      setActiveTab(preferences.homeActiveTab as typeof activeTab);
    }

    // Mark initial load as complete after a short delay, then lazy-load results
    if (!isInitialLoadRef.current) {
      return;
    }

    // Calculate as soon as the saved inputs are in place: `calculatedScores` was
    // just set from them above, which is exactly what "Calculate Score" feeds to
    // the table, so the results are on screen without the user pressing it.
    //
    // Held back until the restored grades have actually been *painted*. A rAF
    // callback runs before the paint of the frame it is queued for, so mounting
    // the table from inside one puts its (second-long) calculation in front of
    // that paint and the saved grades sit invisible until it finishes. Waiting
    // for the frame and then yielding to a task — rAF, then setTimeout, the same
    // order calculateResults() uses — puts the mount after the paint instead.
    let showResultsTaskId: ReturnType<typeof setTimeout> | null = null;
    const showResultsFrameId = requestAnimationFrame(() => {
      showResultsTaskId = setTimeout(() => setShowResults(true), 0);
    });

    const completeInitialLoad = () => {
      isInitialLoadRef.current = false;
    };

    // The idle callback carries a deadline: scoring every programme keeps this page busy for a
    // second at a time, and an idle callback with no timeout can be starved for as long as that
    // goes on — which would leave `isInitialLoadRef` true and suppress every save for the rest of
    // the session.
    const fallbackId = setTimeout(() => {
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        (window as Window & {
          requestIdleCallback: (cb: () => void, options?: { timeout: number }) => number;
        }).requestIdleCallback(completeInitialLoad, { timeout: 1000 });
      } else {
        completeInitialLoad();
      }
    }, 200);

    return () => {
      cancelAnimationFrame(showResultsFrameId);
      if (showResultsTaskId) clearTimeout(showResultsTaskId);
      clearTimeout(fallbackId);
    };
  }, [
    preferences.homeElectiveSubjects,
    preferences.homeSubjectScores,
    preferences.homeActiveTab,
    preferencesLoading,
  ]);

  /**
   * The state "Clear All Selections" puts the DSE inputs back to: no electives,
   * and every core subject blank — no grade selected, no default filled in.
   * "N/A" is the stored "not set" value, and these are the same values
   * clearPreferences() writes to preferences, so the restore effect above
   * agrees with what is on screen instead of fighting it.
   */
  const buildClearedScores = useCallback(() => blankCoreScores(), []);

  // The sidebar's "Clear All Selections" wipes the DSE inputs held in this
  // component's state and then recalculates, so the results table stays in step
  // with the inputs rather than disappearing or keeping results for grades that
  // are no longer on screen.
  const clearSelections = useCallback(() => {
    if (gradeCalcTimerRef.current) {
      clearTimeout(gradeCalcTimerRef.current);
      gradeCalcTimerRef.current = null;
    }

    const clearedScores = buildClearedScores();
    // Record this as the applied state before the preference write below lands. The restore effect
    // never blanks a grade on its own (mergeRestoredScores), so an explicit clear has to be
    // recognised as already applied rather than merged against what is on screen.
    appliedPreferencesSignatureRef.current = restoreFromPreferences({
      homeElectiveSubjects: [],
      homeSubjectScores: clearedScores,
    }).signature;

    setSelectedElectives([]);
    setTempSelectedElectives([]);
    setRemovingSubjects(new Set());
    setScores(clearedScores);

    // Calculate straight from `clearedScores`: calculateResults() reads `scores`
    // from its closure, which is still a render behind at this point.
    setCalculatedScores(clearedScores);
    setActiveTab("all");
    setResultsKey((prev) => prev + 1);
    setShowResults(true);
    updatePreferences({
      homeElectiveSubjects: [],
      homeSubjectScores: clearedScores,
      recommendedRecommendations: [],
      recommendedShowRecommendations: false,
      recommendedAiRecommendation: "",
      homeShowResults: true,
      homeActiveTab: "all",
    });
  }, [buildClearedScores, updatePreferences]);

  const clearSelectionsRef = useRef(clearSelections);
  useEffect(() => {
    clearSelectionsRef.current = clearSelections;
  }, [clearSelections]);

  useEffect(() => {
    const handleClearSelections = () => clearSelectionsRef.current();
    window.addEventListener("clear-selections", handleClearSelections);
    return () => window.removeEventListener("clear-selections", handleClearSelections);
  }, []);

  // Save selected electives to preferences whenever they change (but not during initial load).
  // The grades go with them — see scoresRef — so the stored elective list is never ahead of the
  // stored grades, in memory or in the database.
  useEffect(() => {
    if (isInitialLoadRef.current) return;

    // Save selected electives as subject names
    updatePreferences({
      homeElectiveSubjects: selectedElectives,
      homeSubjectScores: scoresRef.current,
    });
  }, [selectedElectives, updatePreferences]);



  // Subject lists live at module scope (see top of file) — they are static, and rebuilding them on
  // every render also put them out of reach of the state initialisers.

  function toggleElective(subject: { subject: string }) {
    const subjectName = subject.subject;
    const isSelected = tempSelectedElectives.includes(subjectName);

    if (isSelected) {
      setTempSelectedElectives(prev => prev.filter(name => name !== subjectName));
    } else {
      if (tempSelectedElectives.length >= MAX_ELECTIVE_SUBJECTS) return; // limit to 6

      setTempSelectedElectives(prev => [...prev, subjectName]);
    }
  }

  /**
   * Where a grade sits in its subject's list, 0 being the best grade.
   * Blank and "N/A" mean "not set", so they constrain nothing.
   */
  const gradeRank = useCallback((subjectName: string, grade: string): number | null => {
    if (!grade || grade === 'N/A') return null;
    const index = getGradeOptionsForSubject(subjectName).indexOf(grade);
    return index === -1 ? null : index;
  }, []);

  /**
   * Keep current / min / max consistent after an edit, the way a two-handle
   * range control does: the value the user just moved wins, and any bound it
   * passes is carried along with it.
   *
   * Nothing is ever rejected, so pressing "-" on a grade always moves it. This
   * replaces the old "current score cannot be lower than minimum score" toasts,
   * which blocked the edit and left the stepper looking broken.
   */
  const reconcileScoreRange = useCallback(
    (subjectName: string, field: keyof ScoreTriple, next: ScoreTriple): ScoreTriple => {
      const out = { ...next };
      const anchor = gradeRank(subjectName, out[field]);
      // Clearing a value to N/A drops its constraint rather than moving anything.
      if (anchor === null) return out;

      // Best to worst: max, current, min. Reconciling means walking outwards
      // from the edited field and pulling in anything it has overtaken.
      const order: (keyof ScoreTriple)[] = ['max', 'current', 'min'];
      const edited = order.indexOf(field);

      for (let i = edited - 1; i >= 0; i--) {
        const rank = gradeRank(subjectName, out[order[i]]);
        if (rank !== null && rank > anchor) out[order[i]] = out[field];
      }
      for (let i = edited + 1; i < order.length; i++) {
        const rank = gradeRank(subjectName, out[order[i]]);
        if (rank !== null && rank < anchor) out[order[i]] = out[field];
      }

      return out;
    },
    [gradeRank]
  );

  /**
   * The score map an edit produces, as a plain value.
   *
   * Split out of setScore so the new grade exists as something that can be stored *before* it is
   * handed to React — see handleGradeSelect. A functional setScores updater only produces the new
   * map during the render it schedules, which is already too late to be the first thing that
   * happens to it.
   */
  const nextScoresAfterEdit = useCallback(
    (
      currentScores: Record<string, ScoreTriple>,
      subjectName: string,
      field: keyof ScoreTriple,
      value: string
    ): Record<string, ScoreTriple> => {
      const existing = currentScores[subjectName] ?? { current: "", min: "", max: "" };

      // CSD has no meaningful range — its three values move together.
      if (subjectName === "Citizenship and Social Development") {
        return {
          ...currentScores,
          [subjectName]:
            field === "current"
              ? { current: value, min: value, max: value }
              : { ...existing, [field]: value },
        };
      }

      return {
        ...currentScores,
        [subjectName]: reconcileScoreRange(subjectName, field, {
          ...existing,
          [field]: value,
        }),
      };
    },
    [reconcileScoreRange]
  );

  function setScore(subjectName: string, field: keyof ScoreTriple, value: string) {
    const nextScores = nextScoresAfterEdit(scoresRef.current, subjectName, field, value);
    // Assigned here rather than waiting for the effect that mirrors `scores`, so a second click
    // arriving before that effect runs still builds on the first one.
    scoresRef.current = nextScores;
    setScores(nextScores);

    return nextScores;
  }

  function handleGradeSelect(subjectName: string, field: keyof ScoreTriple, value: string) {
    markPreferenceInteraction();
    logPreferenceSave(`level picked — ${subjectName} ${field} = ${value}`);

    const nextScores = setScore(subjectName, field, value);

    /*
     * Store the level first, before anything is done with it.
     *
     * updatePreferences() writes to storage and starts the database request synchronously, so by
     * the time this handler returns the new level is saved. What follows it is a re-score of every
     * programme — a second of blocked main thread — and anything left queued behind that is a
     * second in which a reload reads back the previous level.
     *
     * The effect watching `scores` publishes the same map a moment later; updatePreferences sees
     * the identical value and does nothing with it.
     */
    if (!isInitialLoadRef.current) {
      updatePreferences({ homeSubjectScores: nextScores });
    } else {
      logPreferenceSave("still loading saved grades — this level is not stored yet");
    }

    // Bookkeeping only once the request is away — logActivity serializes the
    // in-memory log synchronously, which is not something to do first.
    logActivity(
      "score-set",
      `Set ${subjectName} ${field} score to ${value}`,
      { subject: subjectName, field, value }
    );

    // Then recalculate, 100ms after the last click so a burst of taps costs one calculation.
    if (gradeCalcTimerRef.current) {
      clearTimeout(gradeCalcTimerRef.current);
    }
    gradeCalcTimerRef.current = setTimeout(() => {
      gradeCalcTimerRef.current = null;
      logPreferenceSave("re-scoring every programme now (the store above already happened)");
      calculateResultsRef.current();
    }, 100);
  }

  // Remove an elective subject with animation
  const removeElective = (subjectName: string) => {
    // Add to removing set to trigger animation
    setRemovingSubjects(prev => new Set(prev).add(subjectName));

    // After animation completes, actually remove the subject
    setTimeout(() => {
      setSelectedElectives(prev => prev.filter(s => s !== subjectName));
      setScores(prev => {
        const newScores = { ...prev };
        delete newScores[subjectName];
        return newScores;
      });
      setRemovingSubjects(prev => {
        const next = new Set(prev);
        next.delete(subjectName);
        return next;
      });
    }, 300); // Match animation duration

    logActivity(
      "elective-removed",
      `Removed elective subject: ${subjectName}`,
      { subject: subjectName }
    );
  };

  // Add a new subject - called by AddNewElective component. The subject starts
  // blank ("N/A" = not set) so the user picks its grade, the same as the core
  // subjects.
  const handleAddSubject = (subject: string) => {
    setSelectedElectives(prev => [...prev, subject]);
    setScores(prev => ({
      ...prev,
      [subject]: { current: 'N/A', min: 'N/A', max: 'N/A' }
    }));

    logActivity(
      "elective-added",
      `Added elective subject: ${subject}`,
      { subject }
    );
  };



  const isElectiveSelected = (subject: { subject: string }) => {
    return tempSelectedElectives.includes(subject.subject);
  };

  const isElectiveDisabled = (subject: { subject: string }) => {
    // If already selected, not disabled
    if (isElectiveSelected(subject)) return false;

    // If at max selections, disabled
    if (!canSelectMore) return true;

    return false;
  };

  const canSelectMore = tempSelectedElectives.length < MAX_ELECTIVE_SUBJECTS;

  // Check if electives have changed
  const electivesHaveChanged = () => {
    if (tempSelectedElectives.length !== selectedElectives.length) return true;
    const tempNames = [...tempSelectedElectives].sort();
    const selectedNames = [...selectedElectives].sort();
    return !tempNames.every((name, i) => name === selectedNames[i]);
  };

  // Open elective dialog - initialize temp state
  const handleOpenElectiveDialog = () => {
    setTempSelectedElectives([...selectedElectives]);
    setShowElectiveDialog(true);
  };

  // Handle dialog close/cancel with confirmation if changes were made
  const handleCloseElectiveDialog = async (confirmed: boolean = false) => {
    if (!confirmed && electivesHaveChanged()) {
      const result = await Swal.fire({
        title: translate('unsavedChanges') || 'Unsaved Changes',
        text: translate('electiveChangesWillBeLost') || 'Your elective selection changes will be lost. Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: translate('yesDiscard') || 'Yes, discard',
        cancelButtonText: translate('keepEditing') || 'Keep editing'
      });

      if (!result.isConfirmed) {
        return; // Don't close dialog
      }
    }

    // Reset temp state and close
    setTempSelectedElectives([...selectedElectives]);
    setShowElectiveDialog(false);
  };

  // Apply changes and update scores
  const handleApplyElectiveChanges = () => {
    if (!electivesHaveChanged()) {
      setShowElectiveDialog(false);
      return;
    }

    // Determine which electives were added/removed
    const oldNames = new Set(selectedElectives);
    const newNames = new Set(tempSelectedElectives);

    const removed = selectedElectives.filter(name => !newNames.has(name));
    const added = tempSelectedElectives.filter(name => !oldNames.has(name));    // Update scores
    setScores(prev => {
      const copy = { ...prev };

      // Remove scores for deselected electives
      removed.forEach(subjectName => {
        delete copy[subjectName];
      });

      // Add scores for new electives with N/A as default
      added.forEach(subjectName => {
        copy[subjectName] = { current: "N/A", min: "N/A", max: "N/A" };
      });

      return copy;
    });

    // Update selected electives
    setSelectedElectives([...tempSelectedElectives]);

    // Trigger re-render of results
    setResultsKey(prev => prev + 1);

    // Close dialog
    setShowElectiveDialog(false);
  };

  ;

  return (
    <div className="pt-6 md:p-6 max-w-full mx-auto">
    
      <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 px-4">
        {appTitle} - {translate("home")}
      </h1>

      {/* DSE Results Section */}
      <DseResultsSection
        translate={translate}
        language={language}
        selectedElectives={selectedElectives}
        maxElectives={MAX_ELECTIVE_SUBJECTS}
        scores={scores}
        csdGrades={csdGrades}
        categoryAGrades={categoryAGrades}
        gradesPending={!inputsReady}
        handleOpenElectiveDialog={handleOpenElectiveDialog}
        setGrade={handleGradeSelect}
        removeElective={removeElective}
        removingSubjects={removingSubjects}
        handleAddSubject={handleAddSubject}
        onCalculate={calculateResults}
        showElectiveDialog={showElectiveDialog}
        handleCloseElectiveDialog={handleCloseElectiveDialog}
        handleApplyElectiveChanges={handleApplyElectiveChanges}
        tempSelectedElectives={tempSelectedElectives}
        groupedSubjects={GROUPED_SUBJECTS}
        isElectiveSelected={isElectiveSelected}
        toggleElective={toggleElective}
        isElectiveDisabled={isElectiveDisabled}
      />

      {/* DSE Cut-Off Marks — withdrawn pending its own data notice (requirement 7).
          The CutOffMarks component is left in place so it can be restored, or
          replaced with a link to the source, once that notice is settled. */}

      {/* Results Table */}
      {showResults && (
        <ResultsTable
          key={resultsKey}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          selectedElectives={selectedElectives}
          scores={calculatedScores}
        />
      )}
    </div>
  );
}
